import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, alpha, CircularProgress, Zoom, TextField, Chip,
  InputAdornment, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { VendasService, IVendaPdvNfce, TSituacaoNfcePdv, ITotaisNfceSessao } from '../../shared/services/api/Venda/venda';
import { SessaoCaixaService } from '../../shared/services/api/Venda/SessaoCaixaService';
import { Environment } from '../../shared/environment';
import { listarVendasPdvOffline } from '../../shared/offline/vendaOffline';
import { useOffline } from '../../shared/offline/useOffline';
import { Dialogsendnfce } from './DialogEmitirnfce';
import { PDVThemeColors } from './PDVThemes';

interface DialogNotasFiscaisPDVProps {
  open: boolean;
  onClose: () => void;
  colors: PDVThemeColors;
  // Mesmo visualizador de PDF do caixa (DialogVisualizarPdfCaixa, em Pdv.tsx).
  onImprimir: (blob: Blob, titulo: string) => void;
  // Sucesso da emissão vai pro header do PDV (ver Dialogsendnfce).
  onMensagemSucesso?: (texto: string) => void;
}

// Venda offline ainda na fila (ver listarVendasPdvOffline em shared/offline).
interface TVendaOffline {
  idmov: string;
  emissao: string;
  total: number;
  nomeParceiro: string;
  pagamentos: { categoria: string; valor: number }[];
  status: 'pendente' | 'erro';
  erro?: string;
}

const TAMANHO_PAGINA = 20;

const formatMoeda = (valor: number) =>
  `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tela "Notas fiscais" do PDV (rodapé): vendas já finalizadas que ficaram
// sem NFC-e — emitidas depois pelo mesmo diálogo do fim da venda
// (Dialogsendnfce) — e as que já têm NFC-e autorizada, pra reimprimir o
// DANFE. Sempre só as vendas da SESSÃO DE CAIXA ATUAL, nunca de outras.
// Vendas feitas offline dessa sessão e ainda não enviadas aparecem em "Sem
// nota" como "Não sincronizada" (a nota só pode ser emitida depois que o
// servidor tiver a venda). Orçamentos não entram (nunca têm nota).
export const DialogNotasFiscaisPDV: React.FC<DialogNotasFiscaisPDVProps> = ({ open, onClose, colors, onImprimir, onMensagemSucesso }) => {
  const [situacao, setSituacao] = useState<TSituacaoNfcePdv>('pendentes');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [vendas, setVendas] = useState<IVendaPdvNfce[]>([]);
  const [vendasOffline, setVendasOffline] = useState<TVendaOffline[]>([]);
  const [semSessao, setSemSessao] = useState(false);
  // Totais da SESSÃO inteira (não só da página/busca exibida): do servidor
  // (emitidas / sem nota) + as vendas offline ainda não sincronizadas.
  const [totais, setTotais] = useState<ITotaisNfceSessao | null>(null);
  const [totalNaoSincronizadas, setTotalNaoSincronizadas] = useState({ quantidade: 0, total: 0 });
  // Recarrega sozinho quando a fila offline muda (ex.: venda sincronizada
  // sai de "Não sincronizada" e passa a vir do servidor).
  const { pendentes } = useOffline();
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [reimprimindo, setReimprimindo] = useState<string | null>(null);
  // Venda escolhida pra emitir — abre o Dialogsendnfce por cima desta tela.
  const [vendaEmitir, setVendaEmitir] = useState<IVendaPdvNfce | null>(null);

  // Busca digitada só vale depois de uma pausa (não consulta a cada tecla).
  useEffect(() => {
    const timer = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => { setPagina(1); }, [situacao, buscaAplicada]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');

    // Sessão atual (a do servidor, ou a aberta offline ainda não enviada).
    const sessao = await SessaoCaixaService.getAtiva();
    const idsessao = sessao && !(sessao instanceof Error) ? sessao.idsessao : '';
    setSemSessao(!idsessao);
    if (!idsessao) {
      setCarregando(false);
      setVendas([]);
      setVendasOffline([]);
      setTotal(0);
      setTotais(null);
      setTotalNaoSincronizadas({ quantidade: 0, total: 0 });
      return;
    }

    const todasOffline = await listarVendasPdvOffline(idsessao);
    setTotalNaoSincronizadas({
      quantidade: todasOffline.length,
      total: todasOffline.reduce((acc, venda) => acc + venda.total, 0),
    });

    const termo = buscaAplicada.toLowerCase();
    const offline = situacao === 'pendentes'
      ? todasOffline.filter((venda) => !termo || venda.nomeParceiro.toLowerCase().includes(termo))
      : [];
    setVendasOffline(offline);

    const [resultado, resultadoTotais] = await Promise.all([
      VendasService.listarNfce(situacao, pagina, buscaAplicada, Environment.IDORCAMENTO || '', TAMANHO_PAGINA),
      VendasService.totaisNfce(Environment.IDORCAMENTO || ''),
    ]);
    setTotais(resultadoTotais instanceof Error ? null : resultadoTotais);
    setCarregando(false);
    if (resultado instanceof Error) {
      // Sem internet: continua mostrando as vendas não sincronizadas.
      setErro(offline.length ? `${resultado.message} Mostrando só as vendas ainda não sincronizadas.` : resultado.message);
      setVendas([]);
      setTotal(0);
      return;
    }
    setVendas(resultado.data);
    setTotal(resultado.totalCount);
  }, [situacao, pagina, buscaAplicada]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar, pendentes]);

  const reimprimir = async (venda: IVendaPdvNfce) => {
    if (!venda.idnfce) return;
    setReimprimindo(venda.idmov);
    const resultado = await VendasService.reimprimirNfce(venda.idnfce);
    setReimprimindo(null);
    if (resultado instanceof Error) {
      setErro(`Não foi possível reimprimir a NFC-e: ${resultado.message}`);
      return;
    }
    onImprimir(resultado, `NFC-e nº ${venda.nfe ?? ''}${venda.serie ? ` (série ${venda.serie})` : ''}`);
  };

  const fecharEmissao = (status?: string) => {
    setVendaEmitir(null);
    // Emitiu: a venda sai da lista "Sem nota" (passa pra "Emitidas").
    if (status === 'success') carregar();
  };

  const totalPaginas = Math.max(1, Math.ceil(total / TAMANHO_PAGINA));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 220 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 48px)',
            width: '94vw',
            maxWidth: '1100px',
          },
        }}
      >
        {/* CABEÇALHO */}
        <Box sx={{
          px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          borderBottom: `0.5px solid ${alpha('#000', 0.08)}`, flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: '8px', background: colors.primaryGradient, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ReceiptLongRoundedIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>Notas fiscais (NFC-e)</Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                Vendas da sessão de caixa atual — emita a nota das que ficaram sem ou reimprima uma NFC-e emitida.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        {/* FILTROS */}
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={situacao}
            onChange={(_, valor: TSituacaoNfcePdv | null) => { if (valor) setSituacao(valor); }}
          >
            <ToggleButton value="pendentes" sx={{ px: 2, fontWeight: 600 }}>Sem nota</ToggleButton>
            <ToggleButton value="emitidas" sx={{ px: 2, fontWeight: 600 }}>Emitidas</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Cliente ou nº da venda"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />

          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={carregar} disabled={carregando}><RefreshRoundedIcon /></IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* TOTAIS DA SESSÃO */}
        {!semSessao && (totais || totalNaoSincronizadas.quantidade > 0) && (
          <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
            {[
              ...(totais ? [
                { label: 'NFC-e emitidas', ...totais.emitidas, cor: '#16A34A', ativo: situacao === 'emitidas' },
                { label: 'Sem nota', ...totais.pendentes, cor: '#F59E0B', ativo: situacao === 'pendentes' },
              ] : []),
              ...(totalNaoSincronizadas.quantidade > 0
                ? [{ label: 'Não sincronizadas', ...totalNaoSincronizadas, cor: '#64748B', ativo: false }]
                : []),
              {
                label: 'Total da sessão',
                quantidade: (totais ? totais.emitidas.quantidade + totais.pendentes.quantidade : 0) + totalNaoSincronizadas.quantidade,
                total: (totais ? totais.emitidas.total + totais.pendentes.total : 0) + totalNaoSincronizadas.total,
                cor: colors.primarySolid,
                ativo: false,
              },
            ].map((card) => (
              <Box
                key={card.label}
                sx={{
                  flex: '1 1 160px',
                  p: 1.25,
                  borderRadius: '10px',
                  border: `1px solid ${alpha(card.cor, card.ativo ? 0.5 : 0.2)}`,
                  bgcolor: alpha(card.cor, card.ativo ? 0.1 : 0.04),
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>{card.label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: card.cor }} noWrap>{formatMoeda(card.total)}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {card.quantidade} venda{card.quantidade === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* LISTA */}
        <Box sx={{ px: 2.5, pb: 2, overflow: 'auto', flexGrow: 1 }}>
          {erro && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>{erro}</Typography>
          )}
          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>
          ) : semSessao ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
              Nenhuma sessão de caixa aberta — abra o caixa para ver as vendas da sessão.
            </Typography>
          ) : vendas.length === 0 && vendasOffline.length === 0 ? (
            !erro && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                {situacao === 'pendentes'
                  ? 'Nenhuma venda sem nota fiscal nesta sessão.'
                  : 'Nenhuma NFC-e emitida nesta sessão.'}
              </Typography>
            )
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Data/hora</TableCell>
                  <TableCell>Venda</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Pagamento</TableCell>
                  {situacao === 'emitidas' && <TableCell>NFC-e</TableCell>}
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Offline, ainda na fila — só na primeira página. */}
                {pagina === 1 && vendasOffline.map((venda) => (
                  <TableRow key={venda.idmov} sx={{ bgcolor: alpha('#F59E0B', 0.06) }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(venda.emissao).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>
                      —
                      {venda.status === 'erro' ? (
                        <Tooltip title={`O servidor recusou esta venda ao sincronizar: ${venda.erro || ''}`}>
                          <Chip label="Recusada na sincronização" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 20 }} />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<CloudOffRoundedIcon sx={{ fontSize: 14 }} />}
                          label="Não sincronizada"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {venda.nomeParceiro}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {venda.pagamentos.map((p) => p.categoria).join(' + ') || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatMoeda(venda.total)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="A NFC-e pode ser emitida assim que a venda for sincronizada com o servidor.">
                        <span>
                          <Button size="small" variant="contained" startIcon={<SendRoundedIcon />} disabled>
                            Emitir NFC-e
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {vendas.map((venda) => (
                  <TableRow key={venda.idmov} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(venda.emissao).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>
                      {venda.numero || '—'}
                      {situacao === 'pendentes' && venda.statusnfce === 1 && (
                        <Tooltip title="Já houve uma tentativa de emissão que não foi autorizada.">
                          <Chip label="Não autorizada" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {venda.nomeParceiro}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {venda.pagamentos.map((p) => p.categoria).join(' + ') || venda.operacao}
                    </TableCell>
                    {situacao === 'emitidas' && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        nº {venda.nfe ?? '—'}{venda.serie ? ` / série ${venda.serie}` : ''}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatMoeda(venda.total)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {situacao === 'pendentes' ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SendRoundedIcon />}
                          onClick={() => setVendaEmitir(venda)}
                          sx={{ background: colors.primaryGradient, color: colors.textOnPrimary, fontWeight: 700, '&:hover': { background: colors.hoverGradient } }}
                        >
                          Emitir NFC-e
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={reimprimindo === venda.idmov ? <CircularProgress size={14} /> : <PrintRoundedIcon />}
                          disabled={reimprimindo !== null}
                          onClick={() => reimprimir(venda)}
                          sx={{ fontWeight: 700 }}
                        >
                          Reimprimir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>

        {/* PAGINAÇÃO */}
        {total > TAMANHO_PAGINA && (
          <Box sx={{
            px: 2.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5,
            borderTop: `0.5px solid ${alpha('#000', 0.08)}`, flexShrink: 0,
          }}>
            <Typography variant="caption" color="text.secondary">
              {total} venda{total > 1 ? 's' : ''} — página {pagina} de {totalPaginas}
            </Typography>
            <Button size="small" disabled={pagina <= 1 || carregando} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
            <Button size="small" disabled={pagina >= totalPaginas || carregando} onClick={() => setPagina((p) => p + 1)}>Próxima</Button>
          </Box>
        )}
      </Dialog>

      {/* Mesmo diálogo de emissão do fim da venda — já vem com a forma de
          pagamento da venda pré-selecionada. */}
      <Dialogsendnfce
        open={!!vendaEmitir}
        selectedId={vendaEmitir?.idmov ?? null}
        onClose={fecharEmissao}
        incluirCpf={false}
        colors={colors}
        formaPagamentoVenda={vendaEmitir?.pagamentos[0]?.categoria}
        isOrcamento={false}
        onMensagemSucesso={onMensagemSucesso}
      />
    </>
  );
};
